"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  AlertTriangle,
  BarChart3,
  Clock3,
  Handshake,
  Rocket,
  TrendingUp,
  UserCheck,
  Users,
} from "lucide-react";
import { api } from "~/trpc/react";

type ActivityRange = "all_time" | "this_week" | "this_month" | "this_year";
type ActivityChannel = "all" | "CHAT" | "PHONE" | "CONTACT_FORM";

const toStartOfDayISOString = (date?: Date) => {
  if (!date) return undefined;
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

const toEndOfDayISOString = (date?: Date) => {
  if (!date) return undefined;
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
};

const getActivityDateRange = (range: ActivityRange) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (range) {
    case "this_week": {
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      return { from: startOfWeek, to: now };
    }
    case "this_month":
      return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: now };
    case "this_year":
      return { from: new Date(now.getFullYear(), 0, 1), to: now };
    case "all_time":
    default:
      return { from: undefined, to: undefined };
  }
};

const formatInteger = (value?: number | null) => {
  const safeValue = typeof value === "number" && !Number.isNaN(value) ? value : 0;
  return Math.round(safeValue).toLocaleString();
};

const formatPercent = (value?: number | null) => {
  const safeValue = typeof value === "number" && !Number.isNaN(value) ? value : 0;
  return `${safeValue.toFixed(1)}%`;
};

const formatMinutesAndSeconds = (value?: number | null) => {
  const safeValue = typeof value === "number" && !Number.isNaN(value) ? value : 0;
  const totalSeconds = Math.round(safeValue * 60);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes} minutes ${seconds} seconds`;
};

const formatDurationFromSeconds = (value?: number | null) => {
  const safeValue = typeof value === "number" && !Number.isNaN(value) ? value : 0;
  const totalSeconds = Math.round(safeValue);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
};

const formatDateTimeShort = (value?: string | Date | null) => {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

type MyActivityPanelProps = {
  projectId: string;
  /** Show Builder shortcut (settings page). Hidden in onboard workspace. */
  showBuilderLink?: boolean;
};

/**
 * Inbox / contacts / phone / token metrics — shared by project Settings and SiteGlue onboard.
 */
export function MyActivityPanel({
  projectId,
  showBuilderLink = false,
}: MyActivityPanelProps) {
  const [conversationRange, setConversationRange] =
    useState<ActivityRange>("all_time");
  const [contactsRange, setContactsRange] = useState<ActivityRange>("all_time");
  const [phoneRange, setPhoneRange] = useState<ActivityRange>("this_month");
  const [tokenRange, setTokenRange] = useState<ActivityRange>("all_time");
  const [conversationChannel, setConversationChannel] =
    useState<ActivityChannel>("all");

  const conversationRangeDates = useMemo(
    () => getActivityDateRange(conversationRange),
    [conversationRange],
  );
  const contactsRangeDates = useMemo(
    () => getActivityDateRange(contactsRange),
    [contactsRange],
  );
  const phoneRangeDates = useMemo(
    () => getActivityDateRange(phoneRange),
    [phoneRange],
  );
  const tokenRangeDates = useMemo(
    () => getActivityDateRange(tokenRange),
    [tokenRange],
  );

  const conversationFrom = toStartOfDayISOString(conversationRangeDates.from);
  const conversationTo = toEndOfDayISOString(conversationRangeDates.to);
  const contactsFrom = toStartOfDayISOString(contactsRangeDates.from);
  const contactsTo = toEndOfDayISOString(contactsRangeDates.to);
  const phoneFrom = toStartOfDayISOString(phoneRangeDates.from);
  const phoneTo = toEndOfDayISOString(phoneRangeDates.to);
  const tokenFrom = toStartOfDayISOString(tokenRangeDates.from);
  const tokenTo = toEndOfDayISOString(tokenRangeDates.to);
  const selectedChannel =
    conversationChannel === "all" ? undefined : conversationChannel;

  const conversationMetricsQuery = api.chat.getConversationMetrics.useQuery(
    {
      projectId,
      channel: selectedChannel as "CHAT" | "PHONE" | "CONTACT_FORM" | undefined,
      from: conversationFrom,
      to: conversationTo,
    },
    { enabled: !!projectId },
  );
  const contactsMetricsQuery = api.contacts.getContactMetrics.useQuery(
    { projectId, from: contactsFrom, to: contactsTo },
    { enabled: !!projectId },
  );
  const phoneUsageQuery = api.chat.getPhoneUsageMetrics.useQuery(
    { projectId, from: phoneFrom, to: phoneTo },
    { enabled: !!projectId },
  );
  const tokenUsageQuery = api.chat.getLlmTokenUsageMetrics.useQuery(
    { projectId, from: tokenFrom, to: tokenTo },
    { enabled: !!projectId },
  );

  const conversationMetrics = conversationMetricsQuery.data;
  const contactsMetrics = contactsMetricsQuery.data;
  const phoneUsage = phoneUsageQuery.data;
  const avgEngagementScore = 43.4;

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold">My Activity</h2>
          <p className="text-sm text-muted-foreground">
            Inbox analytics, contacts analytics, and AgentPhone usage across key
            time ranges.
          </p>
        </div>
        {showBuilderLink ? (
          <Link
            href={`/projects/${projectId}/builder`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline" size="sm">
              <Rocket className="mr-2 h-4 w-4" />
              Builder
            </Button>
          </Link>
        ) : null}
      </div>

      <div className="space-y-4">
        <h3 className="text-2xl font-semibold">Conversations</h3>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-700">Channel</span>
            <Select
              value={conversationChannel}
              onValueChange={(value: ActivityChannel) =>
                setConversationChannel(value)
              }
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder="All Channels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Channels</SelectItem>
                <SelectItem value="CHAT">Chat</SelectItem>
                <SelectItem value="PHONE">Phone</SelectItem>
                <SelectItem value="CONTACT_FORM">Contact Form</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-700">Range</span>
            <Select
              value={conversationRange}
              onValueChange={(value: ActivityRange) =>
                setConversationRange(value)
              }
            >
              <SelectTrigger className="w-36">
                <SelectValue placeholder="All Time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_time">All Time</SelectItem>
                <SelectItem value="this_week">This Week</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="this_year">This Year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="shadow-none border border-gray-200">
            <CardContent className="flex flex-col gap-3 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Conversations</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {conversationMetricsQuery.isLoading
                      ? "…"
                      : formatInteger(conversationMetrics?.totalConversations)}
                  </p>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                  <Users className="h-5 w-5" />
                </div>
              </div>
              <span className="text-sm font-semibold text-gray-500">
                {conversationMetricsQuery.isLoading
                  ? "…"
                  : `${(conversationMetrics?.totalChange ?? 0).toFixed(1)}%`}
              </span>
            </CardContent>
          </Card>
          <Card className="shadow-none border border-gray-200">
            <CardContent className="flex flex-col gap-3 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Team Handoffs</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {conversationMetricsQuery.isLoading
                      ? "…"
                      : formatInteger(conversationMetrics?.teamHandoffs)}
                  </p>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                  <Handshake className="h-5 w-5" />
                </div>
              </div>
              <span className="text-sm font-semibold text-gray-500">
                {conversationMetricsQuery.isLoading
                  ? "…"
                  : `${(conversationMetrics?.teamHandoffsChange ?? 0).toFixed(1)}%`}
              </span>
            </CardContent>
          </Card>
          <Card className="shadow-none border border-gray-200">
            <CardContent className="flex flex-col gap-3 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Abandoned Conversations</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {conversationMetricsQuery.isLoading
                      ? "…"
                      : formatInteger(
                          conversationMetrics?.abandonedConversations,
                        )}
                  </p>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                  <AlertTriangle className="h-5 w-5" />
                </div>
              </div>
              <span className="text-sm font-semibold text-gray-500">
                {conversationMetricsQuery.isLoading
                  ? "…"
                  : `${(conversationMetrics?.abandonedChange ?? 0).toFixed(1)}%`}
              </span>
            </CardContent>
          </Card>
          <Card className="shadow-none border border-gray-200">
            <CardContent className="flex flex-col gap-3 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Conversion Rate</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {conversationMetricsQuery.isLoading
                      ? "…"
                      : formatPercent(conversationMetrics?.conversionRate)}
                  </p>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                  <TrendingUp className="h-5 w-5" />
                </div>
              </div>
              <span className="text-sm font-semibold text-gray-500">
                {conversationMetricsQuery.isLoading
                  ? "…"
                  : `${(conversationMetrics?.conversionRateChange ?? 0).toFixed(1)}%`}
              </span>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-2xl font-semibold">Contacts</h3>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">Range</span>
          <Select
            value={contactsRange}
            onValueChange={(value: ActivityRange) => setContactsRange(value)}
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder="All Time" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all_time">All Time</SelectItem>
              <SelectItem value="this_week">This Week</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="this_year">This Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="shadow-none border border-gray-200">
            <CardContent className="flex flex-col gap-3 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Contacts</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {contactsMetricsQuery.isLoading
                      ? "…"
                      : formatInteger(contactsMetrics?.total)}
                  </p>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                  <Users className="h-5 w-5" />
                </div>
              </div>
              <span className="text-sm font-semibold text-green-600">+56.5%</span>
            </CardContent>
          </Card>
          <Card className="shadow-none border border-gray-200">
            <CardContent className="flex flex-col gap-3 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">New Patients</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {contactsMetricsQuery.isLoading
                      ? "…"
                      : formatInteger(contactsMetrics?.newContacts)}
                  </p>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                  <UserCheck className="h-5 w-5" />
                </div>
              </div>
              <span className="text-sm font-semibold text-green-600">+68.4%</span>
            </CardContent>
          </Card>
          <Card className="shadow-none border border-gray-200">
            <CardContent className="flex flex-col gap-3 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Existing Patients</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {contactsMetricsQuery.isLoading
                      ? "…"
                      : formatInteger(contactsMetrics?.returningContacts)}
                  </p>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                  <Users className="h-5 w-5" />
                </div>
              </div>
              <span className="text-sm font-semibold text-gray-500">9.8%</span>
            </CardContent>
          </Card>
          <Card className="shadow-none border border-gray-200">
            <CardContent className="flex flex-col gap-3 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Avg Engagement Score</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {avgEngagementScore}
                  </p>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                  <BarChart3 className="h-5 w-5" />
                </div>
              </div>
              <span className="text-sm font-semibold text-green-600">+5.1</span>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-2xl font-semibold">Phone Calls</h3>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">Range</span>
          <Select
            value={phoneRange}
            onValueChange={(value: ActivityRange) => setPhoneRange(value)}
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder="All Time" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all_time">All Time</SelectItem>
              <SelectItem value="this_week">This Week</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="this_year">This Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Card className="shadow-none border border-gray-200">
          <CardContent className="flex items-center gap-3 p-4">
            <Clock3 className="h-5 w-5 text-blue-600" />
            <div className="space-y-0.5">
              <p className="text-base font-medium text-gray-900">
                {phoneUsageQuery.isLoading
                  ? "Loading usage..."
                  : formatMinutesAndSeconds(phoneUsage?.totalMinutes)}
              </p>
              {!phoneUsageQuery.isLoading ? (
                <div className="space-y-0.5">
                  <p className="text-xs text-gray-500">
                    {phoneUsage?.totalCalls ?? 0} call
                    {(phoneUsage?.totalCalls ?? 0) === 1 ? "" : "s"} in this range
                  </p>
                  <p className="text-[11px] text-gray-400">
                    Debug: {phoneUsage?.allTimeTotalCalls ?? 0} all-time phone call
                    {(phoneUsage?.allTimeTotalCalls ?? 0) === 1 ? "" : "s"}
                  </p>
                </div>
              ) : null}
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-none border border-gray-200">
          <CardContent className="space-y-3 p-4">
            <h4 className="text-sm font-semibold text-gray-900">Recent Calls</h4>
            {phoneUsageQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">
                Loading recent calls...
              </p>
            ) : (phoneUsage?.recentCalls?.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">
                No recent calls for this range.
              </p>
            ) : (
              <div className="space-y-2">
                {phoneUsage?.recentCalls?.map((call) => (
                  <div
                    key={call.chatId}
                    className="flex items-center justify-between gap-3 rounded-md border border-gray-100 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-gray-800">
                        {call.callerName || "Unknown Caller"}
                      </p>
                      <p className="truncate text-xs text-gray-500">
                        {call.callerPhone || "No caller number"} ·{" "}
                        {formatDateTimeShort(call.startedAt)}
                      </p>
                      <p className="truncate text-[11px] text-gray-400">
                        chatId: {call.chatId} · sessionId:{" "}
                        {call.sessionId || "n/a"}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs font-semibold text-gray-700">
                      {formatDurationFromSeconds(call.durationSeconds)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <h3 className="text-2xl font-semibold">LLM Token Usage</h3>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">Range</span>
          <Select
            value={tokenRange}
            onValueChange={(value: ActivityRange) => setTokenRange(value)}
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder="All Time" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all_time">All Time</SelectItem>
              <SelectItem value="this_week">This Week</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="this_year">This Year</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Card className="shadow-none border border-gray-200">
            <CardContent className="p-4">
              <p className="text-sm text-gray-500">Prompt Tokens</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {tokenUsageQuery.isLoading
                  ? "…"
                  : formatInteger(tokenUsageQuery.data?.promptTokens)}
              </p>
            </CardContent>
          </Card>
          <Card className="shadow-none border border-gray-200">
            <CardContent className="p-4">
              <p className="text-sm text-gray-500">Completion Tokens</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {tokenUsageQuery.isLoading
                  ? "…"
                  : formatInteger(tokenUsageQuery.data?.completionTokens)}
              </p>
            </CardContent>
          </Card>
          <Card className="shadow-none border border-gray-200">
            <CardContent className="p-4">
              <p className="text-sm text-gray-500">Total Tokens</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {tokenUsageQuery.isLoading
                  ? "…"
                  : formatInteger(tokenUsageQuery.data?.totalTokens)}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card className="shadow-none border border-gray-200">
            <CardContent className="space-y-3 p-4">
              <h4 className="text-sm font-semibold text-gray-900">By Channel</h4>
              {tokenUsageQuery.isLoading ? (
                <p className="text-sm text-muted-foreground">Loading...</p>
              ) : (tokenUsageQuery.data?.byChannel?.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No token data for this range.
                </p>
              ) : (
                <div className="space-y-2">
                  {tokenUsageQuery.data?.byChannel.map((row) => (
                    <div
                      key={row.channel}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="font-medium text-gray-700">
                        {row.channel}
                      </span>
                      <span className="text-gray-900">
                        {formatInteger(row.totalTokens)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          <Card className="shadow-none border border-gray-200">
            <CardContent className="space-y-3 p-4">
              <h4 className="text-sm font-semibold text-gray-900">By Backend</h4>
              {tokenUsageQuery.isLoading ? (
                <p className="text-sm text-muted-foreground">Loading...</p>
              ) : (tokenUsageQuery.data?.byBackend?.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No token data for this range.
                </p>
              ) : (
                <div className="space-y-2">
                  {tokenUsageQuery.data?.byBackend.map((row) => (
                    <div
                      key={row.backend}
                      className="flex items-center justify-between gap-3 text-sm"
                    >
                      <span className="truncate font-medium text-gray-700">
                        {row.backend}
                      </span>
                      <span className="shrink-0 text-gray-900">
                        {formatInteger(row.totalTokens)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
